import { useMemo, useState } from 'react'

type QualificacaoRecord = Record<string, unknown> & {
  id?: number
  antigo?: string
  novo?: string
  categoria?: string
  confianca?: string
  observacao?: string | null
  frases?: string[]
  sinonimos?: string[]
}

type RankedQualificacao = QualificacaoRecord & {
  score: number
  reasons: string[]
}

type Source = 'online' | 'cache'

const CHIPS = [
  'troca de titularidade',
  'segunda via',
  'paguei no totem',
  'paguei e quero religar',
  'parcelamento',
  'consumo elevado',
  'tarifa social',
  'ligação nova de água',
  'água suja',
  'status da solicitação',
]

const STOPWORDS = new Set(['de', 'da', 'do', 'das', 'dos', 'a', 'o', 'e', 'em', 'para', 'por', 'na', 'no', 'com', 'uma', 'um', 'as', 'os', 'que'])

const SYNONYM_GROUPS: Record<string, string[]> = {
  religacao: ['religacao', 'religiacao', 're ligacao', 're-ligacao', 'religar', 'religue', 'religo'],
  debito: ['debito', 'debito pago', 'divida', 'pendencia', 'pendencias'],
  titularidade: ['titularidade', 'troca de titularidade', 'mudar titular', 'conta no meu nome', 'passar pro meu nome'],
  segundavia: ['segunda via', '2 via', '2a via', '2 via da conta', 'segunda via da fatura'],
  pagamento: ['paguei', 'pagamento', 'pago', 'quitado', 'ja paguei'],
  totem: ['totem', 'pagamento no totem', 'paguei no totem'],
  vazamento: ['vazamento', 'agua vazando', 'vazando'],
  tarifasocial: ['tarifa social', 'beneficio social'],
  consumoalto: ['consumo elevado', 'conta alta', 'fatura alta', 'consumo alto'],
  status: ['status', 'andamento', 'consulta', 'acompanhar'],
  agua: ['agua', 'água'],
  esgoto: ['esgoto'],
  cadastro: ['cadastro', 'dados cadastrais', 'atualizacao cadastral'],
}

const CATEGORY_BOOSTS: Record<string, string[]> = {
  Financeiro: ['pagamento', 'debito', 'divida', 'parcelamento', 'fatura', 'conta'],
  Cadastro: ['cadastro', 'titular', 'telefone', 'email', 'cpf', 'rg'],
  Abastecimento: ['corte', 'religacao', 'agua', 'pressao'],
  Serviços: ['ligacao', 'esgoto', 'agua', 'cavalete', 'vazamento'],
  Benefício: ['tarifa', 'social'],
  Informação: ['status', 'andamento', 'consulta', 'informacao'],
}

function normalizeText(value: unknown) {
  return String(value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase('pt-BR')
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

function tokenize(value: unknown) {
  return normalizeText(value)
    .split(' ')
    .filter((token) => token && !STOPWORDS.has(token))
}

function levenshtein(a: string, b: string) {
  if (a === b) return 0
  if (!a.length) return b.length
  if (!b.length) return a.length
  const matrix = Array.from({ length: a.length + 1 }, () => Array<number>(b.length + 1).fill(0))
  for (let i = 0; i <= a.length; i += 1) matrix[i][0] = i
  for (let j = 0; j <= b.length; j += 1) matrix[0][j] = j
  for (let i = 1; i <= a.length; i += 1) {
    for (let j = 1; j <= b.length; j += 1) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1
      matrix[i][j] = Math.min(
        matrix[i - 1][j] + 1,
        matrix[i][j - 1] + 1,
        matrix[i - 1][j - 1] + cost,
      )
    }
  }
  return matrix[a.length][b.length]
}

function expandSynonyms(term: string) {
  const query = normalizeText(term)
  const expansions = new Set(query ? [query] : [])
  let detectedCount = 0

  Object.values(SYNONYM_GROUPS).forEach((group) => {
    const found = group.some((item) => {
      const normalized = normalizeText(item)
      return query.includes(normalized) || normalized.includes(query)
    })
    if (found) {
      group.forEach((item) => expansions.add(normalizeText(item)))
      detectedCount += group.length
    }
  })

  tokenize(query).forEach((token) => {
    Object.values(SYNONYM_GROUPS).forEach((group) => {
      const found = group.some((item) => {
        const normalized = normalizeText(item)
        return token === normalized || levenshtein(token, normalized) <= 2
      })
      if (found) group.forEach((item) => expansions.add(normalizeText(item)))
    })
  })

  return { terms: Array.from(expansions), detectedCount }
}

function scoreTokenAgainstText(token: string, textTokens: string[], textString: string) {
  let points = 0
  if (textString.includes(token)) points += 18
  textTokens.forEach((candidate) => {
    if (token === candidate) points += 18
    else {
      const distance = levenshtein(token, candidate)
      if (distance === 1) points += 10
      else if (distance === 2 && token.length >= 6) points += 5
    }
  })
  return points
}

function recordStrings(record: QualificacaoRecord) {
  return {
    antigo: String(record.antigo ?? '—'),
    novo: String(record.novo ?? ''),
    categoria: String(record.categoria ?? ''),
    confianca: String(record.confianca ?? ''),
    observacao: String(record.observacao ?? ''),
    frases: Array.isArray(record.frases) ? record.frases.map(String) : [],
    sinonimos: Array.isArray(record.sinonimos) ? record.sinonimos.map(String) : [],
  }
}

function scoreItem(record: QualificacaoRecord, term: string, expandedTerms: string[]) {
  const query = normalizeText(term)
  if (!query) return { score: 0, reasons: [] as string[] }
  const item = recordStrings(record)
  const fields = [item.antigo, item.novo, item.categoria, item.observacao, ...item.frases, ...item.sinonimos].join(' | ')
  const textString = normalizeText(fields)
  const textTokens = tokenize(fields)
  const queryTokens = Array.from(new Set(tokenize(query)))
  const allTokens = Array.from(new Set([...queryTokens, ...expandedTerms.flatMap((value) => tokenize(value))]))

  let score = 0
  const reasons: string[] = []

  if (normalizeText(item.novo) === query) {
    score += 220
    reasons.push('classificação nova exata')
  }
  if (normalizeText(item.antigo) === query) {
    score += 200
    reasons.push('serviço antigo exato')
  }
  if (item.frases.some((value) => normalizeText(value) === query)) {
    score += 180
    reasons.push('fala do cliente exata')
  }
  if (textString.includes(query) && query.length > 2) {
    score += 95
    reasons.push('frase encontrada no texto')
  }
  expandedTerms.forEach((value) => {
    if (value !== query && value.length > 2 && textString.includes(value)) score += 22
  })
  allTokens.forEach((token) => {
    if (token.length >= 2) score += scoreTokenAgainstText(token, textTokens, textString)
  })
  if (queryTokens.length > 1 && queryTokens.every((token) => textString.includes(token))) {
    score += 45
    reasons.push('todos os termos principais presentes')
  }

  const boosts = CATEGORY_BOOSTS[item.categoria] ?? []
  const matchingBoosts = boosts.filter((token) => allTokens.includes(token))
  if (matchingBoosts.length) score += matchingBoosts.length * 8

  return { score, reasons }
}

function buildCopySummary(item: RankedQualificacao) {
  const data = recordStrings(item)
  return [
    `CLASSIFICAÇÃO: ${data.novo}`,
    `SERVIÇO ANTIGO: ${data.antigo}`,
    `CATEGORIA: ${data.categoria}`,
    `CONFIANÇA: ${data.confianca}`,
    data.observacao ? `ORIENTAÇÃO: ${data.observacao}` : '',
  ].filter(Boolean).join('\n')
}

async function copyText(value: string) {
  await navigator.clipboard.writeText(value)
}

function ResultCard({ item, main, onCopied }: { item: RankedQualificacao; main?: boolean; onCopied: (message: string) => void }) {
  const data = recordStrings(item)
  const reasons = item.reasons.slice(0, 2)
  const examples = data.frases.slice(0, 2)

  async function copyClassification() {
    try {
      await copyText(data.novo)
      onCopied('Classificação copiada.')
    } catch {
      onCopied('Não foi possível copiar automaticamente.')
    }
  }

  async function copySummary() {
    try {
      await copyText(buildCopySummary(item))
      onCopied('Resumo copiado.')
    } catch {
      onCopied('Não foi possível copiar automaticamente.')
    }
  }

  return (
    <article className={`qlf8-result-card ${main ? 'is-main' : ''}`}>
      <div className="qlf8-result-top">
        <div>
          <span className="qlf8-old">Serviço antigo: {data.antigo || '—'}</span>
          <h3>{data.novo}</h3>
        </div>
        <span className="qlf8-score">Match {Math.min(item.score, 999)}</span>
      </div>
      {data.observacao && <p className="qlf8-observation">{data.observacao}</p>}
      <div className="qlf8-tags">
        <span>Categoria: {data.categoria || '—'}</span>
        <span>Confiança: {data.confianca || '—'}</span>
        <span>Motivo: {reasons.length ? reasons.join(' • ') : 'aderência por termos relacionados'}</span>
      </div>
      <div className="qlf8-examples"><strong>Exemplos:</strong> {examples.length ? examples.join(' • ') : '—'}</div>
      <div className="qlf8-actions">
        <button type="button" className="primary-button compact" onClick={() => void copyClassification()}>Copiar classificação</button>
        <button type="button" className="secondary-button compact" onClick={() => void copySummary()}>Copiar resumo</button>
      </div>
    </article>
  )
}

export function QualificacoesClassifier({ records, source, savedAt, loading, error }: {
  records: Record<string, unknown>[]
  source: Source
  savedAt: string | null
  loading: boolean
  error: string
}) {
  const [query, setQuery] = useState('')
  const [message, setMessage] = useState('')
  const expanded = useMemo(() => expandSynonyms(query), [query])

  const ranked = useMemo<RankedQualificacao[]>(() => {
    const term = query.trim()
    if (!term) return []
    return (records as QualificacaoRecord[])
      .map((record) => ({ ...record, ...scoreItem(record, term, expanded.terms) }))
      .filter((item) => item.score > 0)
      .sort((a, b) => b.score - a.score || String(a.novo ?? '').localeCompare(String(b.novo ?? ''), 'pt-BR'))
      .slice(0, 5)
  }, [records, query, expanded.terms])

  const main = ranked[0]
  const alternatives = ranked.slice(1)
  const queryTokens = tokenize(query)

  return (
    <section className="operational-tool qlf8">
      <div className="tool-heading">
        <div>
          <p className="eyebrow">CLASSIFICADOR OPERACIONAL</p>
          <h2>Qualificações</h2>
          <p className="muted">Localize a classificação nova pela fala do cliente, nome antigo do serviço, palavras-chave e sinônimos.</p>
        </div>
        <span className={`status-chip ${source === 'cache' ? 'warning' : ''}`}>{source === 'cache' ? 'Base em contingência' : 'Base publicada'}</span>
      </div>

      <div className="qlf8-layout">
        <section className="qlf8-search-card">
          <label className="qlf8-search-label">Fala do cliente / serviço antigo / palavra-chave
            <input
              type="search"
              placeholder="Ex.: paguei e quero religar minha água"
              value={query}
              onChange={(event) => { setQuery(event.target.value); setMessage('') }}
              autoComplete="off"
            />
          </label>
          <div className="qlf8-search-actions">
            <button type="button" className="secondary-button compact" onClick={() => { setQuery(''); setMessage('') }}>Limpar busca</button>
            <span>{records.length} qualificações disponíveis{source === 'cache' && savedAt ? ` · cache ${new Date(savedAt).toLocaleString('pt-BR')}` : ''}</span>
          </div>
          <div className="qlf8-quick">
            <strong>Buscas rápidas</strong>
            <div>{CHIPS.map((chip) => <button type="button" key={chip} onClick={() => { setQuery(chip); setMessage('') }}>{chip}</button>)}</div>
          </div>
          <div className="qlf8-stats">
            <div><strong>{ranked.length}</strong><span>resultado(s)</span></div>
            <div><strong>{queryTokens.length}</strong><span>termo(s) principal(is)</span></div>
            <div><strong>{expanded.detectedCount}</strong><span>variações detectadas</span></div>
          </div>
          <div className="notice info">O classificador orienta a escolha com base na base oficial. Quando houver dúvida entre resultados próximos, use a observação, a categoria e o contexto do atendimento.</div>
        </section>

        <section className="qlf8-results-area">
          {loading && <div className="empty-state">Carregando qualificações...</div>}
          {error && <div className="notice error" role="alert">{error}</div>}
          {!loading && !error && !query.trim() && <div className="qlf8-empty"><strong>Comece digitando uma busca.</strong><span>Você pode pesquisar pela fala do cliente, pelo nome antigo do serviço ou pela classificação nova.</span></div>}
          {!loading && !error && query.trim() && !main && <div className="qlf8-empty"><strong>Nenhum resultado encontrado.</strong><span>Tente termos mais simples, como segunda via, religação, tarifa social ou vazamento.</span></div>}
          {!loading && !error && main && (
            <>
              <div className="qlf8-bucket-head"><span className="main">Mais provável</span><small>{expanded.terms.length > 1 ? `Sinônimos e variações considerados: ${expanded.terms.slice(0, 8).join(', ')}` : 'Busca direta sem expansão relevante.'}</small></div>
              <ResultCard item={main} main onCopied={setMessage} />
              {alternatives.length > 0 && (
                <section className="qlf8-alternatives">
                  <div className="qlf8-bucket-head"><span className="alt">Alternativas possíveis</span><small>Use estas opções quando a conversa do cliente indicar uma nuance diferente do caso principal.</small></div>
                  {alternatives.map((item) => <ResultCard key={String(item.id ?? `${item.novo}-${item.score}`)} item={item} onCopied={setMessage} />)}
                </section>
              )}
            </>
          )}
          {message && <div className="tool-status ok">{message}</div>}
        </section>
      </div>
    </section>
  )
}
